import { useEffect, useRef } from 'react';

/**
 * The detail half: what this browser is running, and what the API answering it is running.
 *
 * Built entirely on the shared tokens — `bg-surface`, `border-line`, `text-ink-*` — which every
 * direction file defines and `.onair` redefines. That is what lets one dialog serve The Ledger,
 * The Rail and the inverted on-air palette without a flag saying which it is in.
 *
 * Presentational only, like everything in `points/`: it fetches nothing and knows which app it is
 * in only from the label it is handed. The API's version is a request, so the app that can make
 * one passes the result in.
 */

export interface VersionFacts {
  version: string;
  /** Full SHA. Shortened here for display, never on the wire. */
  commit?: string | null;
  /** ISO 8601. */
  builtAt?: string | null;
}

/**
 * A request has three outcomes and the dialog words all three. An API that cannot be reached is
 * not an API running an unknown version — the first sends somebody to check the deployment, the
 * second to check the build.
 */
export type ApiVersionState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; facts: VersionFacts; environment?: string | null; service?: string | null };

/**
 * Folds a query result into the three states above. Shared so the three apps cannot word the
 * same three outcomes differently — the shape is declared structurally rather than imported from
 * TanStack, which is how `Rundown` and the points designer already take their inputs.
 *
 * Error is checked before data: a refetch that fails leaves the previous answer in `data`, and
 * reporting a version the API is no longer known to be serving is worse than saying so.
 */
export function apiVersionState(query: {
  isError: boolean;
  data?: (VersionFacts & { environment?: string | null; service?: string | null }) | null;
}): ApiVersionState {
  if (query.isError) return { status: 'error' };
  if (!query.data) return { status: 'loading' };
  return {
    status: 'ready',
    facts: query.data,
    environment: query.data.environment,
    service: query.data.service,
  };
}

/**
 * `2026-08-16 14:22 UTC`. Deliberately not localised: this is a diagnostics panel read aloud and
 * pasted into tickets, where "16/08/2026" and "08/16/2026" are the same ambiguity the tournament
 * importer refuses dates over.
 */
function formatBuiltAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return `${at.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-4 py-1">
      <dt className="w-24 shrink-0 text-[11px] uppercase tracking-[0.1em] text-ink-800">{label}</dt>
      <dd className="num min-w-0 break-all text-[12.5px] text-ink-950">{value}</dd>
    </div>
  );
}

function Facts({ facts, extra }: { facts: VersionFacts; extra?: Array<[string, string]> }) {
  const built = formatBuiltAt(facts.builtAt);
  return (
    <dl>
      <Row label="Version" value={facts.version} />
      {/* Absent rather than blank: "—" beside Commit reads as a build with no commit, which is
          not a thing. A build with no checkout to read one from simply does not report it. */}
      {facts.commit ? <Row label="Commit" value={facts.commit.slice(0, 7)} /> : null}
      {built ? <Row label="Built" value={built} /> : null}
      {extra?.map(([label, value]) => <Row key={label} label={label} value={value} />)}
    </dl>
  );
}

export function AboutDialog({
  appLabel,
  client,
  api,
  onClose,
}: {
  /** What this SPA is called to the person reading, e.g. "Room console". */
  appLabel: string;
  client: VersionFacts;
  api: ApiVersionState;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The single shared version number is what makes this comparison possible, and reporting the
  // mismatch is most of why it is shared: a SPA a deploy left behind looks exactly like a working
  // one until something it calls has moved on.
  const mismatch = api.status === 'ready' && api.facts.version !== client.version;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-md overflow-y-auto border border-line bg-surface"
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <h2 id="about-dialog-title" className="font-display text-base font-semibold text-ink-950">
            About Brush
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="shrink-0 border border-line px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-800 transition hover:text-ink-950"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-4">
          <h3 className="mb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-ink-800">
            {appLabel}
          </h3>
          <Facts facts={client} />

          <h3 className="mt-5 mb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-ink-800">
            API
          </h3>
          {api.status === 'loading' ? (
            <p className="py-1 text-[12.5px] text-ink-800">Checking…</p>
          ) : api.status === 'error' ? (
            <p className="py-1 text-[12.5px] text-ink-800">
              Couldn&rsquo;t reach the API to ask which build it is running.
            </p>
          ) : (
            <Facts
              facts={api.facts}
              extra={[
                ...(api.service ? ([['Service', api.service]] as Array<[string, string]>) : []),
                ...(api.environment
                  ? ([['Environment', api.environment]] as Array<[string, string]>)
                  : []),
              ]}
            />
          )}

          {mismatch ? (
            <p className="mt-4 border border-line px-3 py-2 text-[12px] text-ink-800">
              This app and the API are on different versions. Reloading the page picks up a newer
              app; if that doesn&rsquo;t change it, one of the two hasn&rsquo;t been deployed yet.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
